/**
 * Whether this drone can be commanded right now, and if not, why not.
 *
 * The command bar renders the reason rather than a row of dead grey buttons.
 * A disabled control with no explanation makes the operator guess whether the
 * aircraft is unreachable, the account lacks permission, or the UI is broken —
 * and guessing is exactly what a command surface must not require. This mirrors
 * the hardwareBlockReason convention the dock sidebar already uses.
 */
import { linkFreshness } from './drone-telemetry';
import { manualPortraitRefusal } from './manual-touch';
import {
  MANUAL_LIMITS,
  MANUAL_REFUSAL,
  refusalHeldBy,
  refusalStaleTelemetry,
  type PointerMode,
} from './manual-vector';
import type { DroneCommandAction } from '@/hooks/useDroneCommand';
import type { LiveDrone } from '@/hooks/useWebSocket';
import type { ManualControlHold } from '@/types/coordination';
import type { CompanionStatus, PayloadCapabilities } from '@/types/fleet';
import { COMMAND_KEY_LABEL } from '@/lib/copy';

// MAVLINK_ONLY_COMMANDS is gone: its ['pause','resume','goto'] list was only ever
// applied to airframes that route to no executor at all, which are now blocked
// outright rather than offered a bar with three keys missing. The one commandable
// route that lacks verbs (dji_companion, no pause/hold) names them at its own
// branch below — goto is NOT among them, since the companion flies it as a
// self-contained wayline.

/**
 * Which executor the API will route this drone's flight commands to.
 *
 * Mirrors `CommandRoute` in src/services/drone-executor.ts. Kept as a widened
 * `string` on the wire (see `commandRouteOf`) so an API that learns a new route
 * name cannot crash a frontend that has not shipped yet.
 */
export type CommandRoute = 'mavlink' | 'dji_companion' | 'dji_cloud' | 'skydio' | 'unsupported';

const KNOWN_ROUTES: ReadonlySet<string> = new Set<CommandRoute>([
  'mavlink', 'dji_companion', 'dji_cloud', 'skydio', 'unsupported',
]);

/**
 * The route the server resolved for this drone, or null when we cannot know it.
 *
 * The server resolves this with `resolveCommandRoute` — the same function the
 * command endpoint routes on — so the COP and the API cannot disagree about what
 * will fly. The COP used to re-derive the vendor rule itself from `manufacturer`
 * plus `integration_config.companion`, a flag nothing in the product ever writes:
 * every DJI registered through the fleet UI was mislabelled a Cloud aircraft and
 * lost its command bar, even though the API would have flown it.
 *
 * Two ways this returns null, and both mean "do not gate on the vendor":
 * no row at all (registry still loading, or the fetch failed), and a route name
 * this frontend does not recognise. Treating an unrecognised route as blocked
 * would let a future API rename ground the whole fleet from the COP.
 *
 * The `manufacturer` fallback is for one deploy window only: frontend up, API not
 * yet sending the field. It reproduces the OLD client-side derivation exactly, so
 * that window behaves as it does today rather than in some third way.
 */
export function commandRouteOf(
  row: {
    command_route?: string | null;
    manufacturer?: string | null;
    integration_config?: Record<string, unknown> | null;
  } | null,
): CommandRoute | null {
  if (!row) return null;
  if (row.command_route) {
    return KNOWN_ROUTES.has(row.command_route) ? (row.command_route as CommandRoute) : null;
  }

  // Deploy-window fallback. Deliberately the old rule, including its bug: with
  // no route from the server, a DJI with no hand-set `companion` flag still reads
  // as Cloud. Inventing a better answer here would mean two client derivations to
  // keep in step, which is the drift this function exists to end.
  switch (row.manufacturer) {
    case 'dji':
      return row.integration_config?.companion === true ? 'dji_companion' : 'dji_cloud';
    case 'autel':
    case 'mavlink':
      return 'mavlink';
    case 'skydio':
      return 'skydio';
    // Not 'unsupported': without the server's verdict we have not established
    // that nothing can fly this, and a null here suppresses vendor gating rather
    // than asserting a block.
    default:
      return null;
  }
}

/** Is this drone flown by our MSDK companion app on the RC? */
export function isCompanionCommanded(row: Parameters<typeof commandRouteOf>[0]): boolean {
  return commandRouteOf(row) === 'dji_companion';
}

/**
 * Does this drone have an HMAC command secret, per the server (#261)?
 *
 * The secret itself is redacted from every drone response; the API sends
 * `integration_config.command_secret_present` in its place. Without one, the
 * companion app cannot sign, and `MissionController` refuses every motor
 * command - so ARM is a dead key and the operator only finds out as a refusal.
 *
 * Null means UNKNOWN, not absent: an older API, a registry still loading, or a
 * drone with no `integration_config` at all. Only an explicit `false` is
 * evidence, for the same reason `commandRouteOf` returns null rather than
 * guessing - telling an operator their aircraft cannot be armed because a fetch
 * has not landed is a false statement about their aircraft.
 */
export function commandSecretPresentOf(
  row: Parameters<typeof commandRouteOf>[0],
): boolean | null {
  const value = row?.integration_config?.command_secret_present;
  return typeof value === 'boolean' ? value : null;
}

export interface CommandAvailability {
  /** False when no command at all should be offered. */
  canCommand: boolean;
  /** Operator-facing explanation when canCommand is false. */
  blockedReason: string | null;
  /** Commands this airframe or state cannot run even though it is commandable. */
  unsupported: ReadonlySet<DroneCommandAction>;
  unsupportedReason: string | null;
  /** Set when the drone has a home dock, so the bar can offer it. */
  dockId: string | null;
}

const NONE: ReadonlySet<DroneCommandAction> = new Set();

export function droneCommandAvailability({
  drone,
  commandRoute = null,
  commandSecretPresent = null,
  dockId = null,
  canOperate,
  now = Date.now(),
}: {
  drone: LiveDrone;
  /**
   * From `commandRouteOf(registry row)` — the server's own routing verdict, never
   * re-derived from `manufacturer` here.
   *
   * Null means we cannot know it: the registry is still loading, its fetch
   * failed, or the API sent a route name this frontend does not recognise. Vendor
   * gating is suppressed in that case — telling an operator a MAVLink aircraft is
   * "MAVLink only, not supported" because a fetch has not landed yet is a false
   * statement about their aircraft.
   */
  commandRoute?: CommandRoute | null;
  /**
   * From `commandSecretPresentOf(registry row)` (#261). Null means unknown and
   * gates nothing; only an explicit `false` blocks.
   */
  commandSecretPresent?: boolean | null;
  dockId?: string | null;
  /** Role gate — admin or operator. */
  canOperate: boolean;
  now?: number;
}): CommandAvailability {
  const blocked = (blockedReason: string, dock: string | null = null): CommandAvailability => ({
    canCommand: false,
    blockedReason,
    unsupported: NONE,
    unsupportedReason: null,
    dockId: dock,
  });

  if (!canOperate) {
    return blocked('Your role can view the air picture but not command aircraft.');
  }

  // A RemoteID beacon is a one-way broadcast. There is no control link to send
  // anything down, and never will be for this track.
  if (drone.telemetry_source === 'remoteid') {
    return blocked('RemoteID broadcast track — no control link. Observation only.');
  }

  if (linkFreshness(drone.last_seen_at, now) === 'lost') {
    return blocked('Telemetry lost — commands would not be acknowledged.', dockId);
  }

  // Three of the five routes reach an executor that rejects every verb — skydio
  // and dji_cloud are NotImplementedExecutor (501), and unsupported makes
  // getExecutor throw, which the command endpoint turns into a 422. Offering live
  // RTH/LAND/ABORT keys that are guaranteed to fail is the exact "dead button"
  // this surface exists to avoid — worse than a disabled key, because it looks
  // operational.
  //
  // Gated on the route the SERVER resolved, not on a manufacturer string re-read
  // here. The old manufacturer form blocked skydio and non-companion dji and
  // nothing else, so `manufacturer: 'other'` — registerable through the fleet UI —
  // got a full live bar whose every key 422s.
  switch (commandRoute) {
    case 'skydio':
      return blocked('No flight executor for Skydio yet — observation only.', dockId);
    case 'dji_cloud':
      // Does NOT say "command from the dock panel": the dock's own flight routes
      // resolve through the same getExecutor and 501 identically, so that advice
      // sent the operator to a second dead end. The RC is the real answer.
      return blocked(
        'DJI Cloud aircraft cannot be commanded by SHOVRON yet — fly it from the DJI RC.',
        dockId,
      );
    case 'unsupported':
      return blocked('No flight executor for this airframe — observation only.', dockId);
    default:
      break;
  }

  // #261. A companion aircraft with no HMAC secret cannot sign, and
  // MissionController refuses every unsigned motor command — so ARM is a dead
  // key in the same way the routes above are, and for a reason the operator has
  // no other way to see. Before this it read as an unexplained refusal, arriving
  // after the dock had already opened its bay.
  //
  // Only an explicit `false` blocks. Null is "we do not know" (older API,
  // registry still loading, no integration_config at all), and suppressing the
  // bar on a missing field would tell operators their aircraft is broken every
  // time a fetch is slow.
  if (commandRoute === 'dji_companion' && commandSecretPresent === false) {
    return blocked(
      'No command secret on this drone — the RC app cannot sign, so every command would be refused.',
      dockId,
    );
  }

  const unsupported = new Set<DroneCommandAction>();
  const reasons: string[] = [];

  // Only gate the vendor-specific verbs once the route is actually known.
  if (commandRoute === 'dji_companion') {
    // The companion app flies goto as a self-contained wayline; only pause/hold
    // have no DJI verb. ABORT is the hold-equivalent — it stops the mission and
    // hands back the sticks without landing — so it is named here rather than
    // steering the operator to the two verbs that end the sortie.
    for (const cmd of ['pause', 'resume'] as const) unsupported.add(cmd);
    reasons.push(
      // Names the key by the words on it. The key stopped saying ABORT in #505's T8 pass,
      // and a refusal that points at a key nobody can find is worse than no refusal.
      `No hold on the DJI companion. "${COMMAND_KEY_LABEL.abort}" holds it and returns the sticks.`,
    );
  }
  // Landing confirmation is a DJI concept: only the companion app has a verb for
  // it (KeyConfirmLanding), and the MAVLink bridge answers with a rejection. A
  // key that is guaranteed to fail is the dead button this file exists to
  // prevent. An UNKNOWN route still gets the key - see the note above about not
  // claiming a vendor reason we have not read.
  if (commandRoute === 'mavlink') {
    unsupported.add('confirm_landing');
    reasons.push('Landing confirmation is DJI-only - a MAVLink aircraft has no held-landing state to clear.');
  }
  // No "MAVLink only" branch left, and that is the point rather than an omission.
  // Every route that reaches here is one an executor actually flies: `mavlink`
  // implements all nine verbs, `dji_companion` all but pause/resume, and the three
  // routes with no executor returned blocked above. The old code needed a
  // catch-all because unknown manufacturers fell through as commandable — under
  // route gating they are named and blocked instead.

  // NOTE: an on-ground reading deliberately does NOT gate anything here.
  // `status` is `is_flying === true || altitude > 2` (useWebSocket), so an
  // aircraft hovering low, descending through 2 m, or reporting one bad altitude
  // sample STILL reads as grounded whenever it cannot answer for itself - which
  // is every source but the DJI companion app. Removing LAND, RTH or ABORT from
  // an aircraft that is actually airborne is the worst thing this surface could
  // do, so the reading is not trusted to gate them however it is derived.
  return {
    canCommand: true,
    blockedReason: null,
    unsupported,
    unsupportedReason: reasons[0] ?? null,
    dockId,
  };
}

// ---------------------------------------------------------------------------
// Manual flight control — a second, stricter gate on the same aircraft.
//
// Manual flight is not another one-shot verb, so it is not in the table above:
// it takes an exclusive authority lock, it streams motor commands, and it is
// refused for reasons that would never stop an RTH. Every string it renders is
// fixed by docs/PLANS/manual-flight-control/WIRE-CONTRACT.md and re-exported
// from manual-vector.ts, so the operator reads the SAME sentence whether it was
// the browser, the API or the RC that refused — see MANUAL_REFUSAL there.
// ---------------------------------------------------------------------------

/**
 * The altitude bar, and since #338 only HALF of the answer — see `isAirborne` below.
 *
 * The backend applies the same rule (`manualPreflight` in src/api/drones.ts, through the shared
 * `isPointAirborne`) and answers 422 with the identical string, so the two must move together or
 * never. Refusing here just turns a round trip into an immediate answer.
 */
const AIRBORNE_ABOVE_M = 2;

/**
 * Airborne enough to be handed the sticks.
 *
 * The aircraft's own answer first, the bar as the fallback — the same rule the server applies, and
 * behind it the RC, which is the gate that actually matters: `if (!telemetry.isFlying) return
 * Denied(ON_GROUND)` in `ControlAuthority.kt`. On the bar alone this greyed the MANUAL key out at
 * 1.9 m for an aircraft the RC would have flown, and after #338 it did so while the COP painted
 * that same aircraft FLYING two panels away.
 *
 * Absent means the source cannot answer, never "no", so a fleet that does not publish the field
 * keeps exactly the behaviour it had. `false` from an aircraft at altitude does not ground it
 * either: the bar still has the last word in that direction.
 *
 * Exported since #505 slice 10: the command bar's phase filter asks the same
 * question to decide which verbs the phase can accept, and a second, subtly
 * different airborne test in this same file is exactly how the rule above stops
 * moving together with the server's (D36).
 */
export function isAirborne(drone: LiveDrone): boolean {
  return drone.is_flying === true || drone.altitude > AIRBORNE_ABOVE_M;
}

/**
 * The aircraft says it is on the ground, and the altitude agrees.
 *
 * **Not the negation of `isAirborne`, deliberately.** `!isAirborne` is true for
 * an aircraft that simply cannot answer - which is every source but the DJI
 * companion app, since `mavlink-adapter` publishes `armed` and no flight-state
 * field at all. Reading that silence as "on the ground" is what the NOTE above
 * `droneCommandAvailability`'s return refuses to do, in its words: an aircraft
 * hovering low, descending through 2 m, or reporting one bad altitude sample
 * still reads as grounded whenever it cannot answer for itself, and "removing
 * LAND, RTH or ABORT from an aircraft that is actually airborne is the worst
 * thing this surface could do".
 *
 * So this requires POSITIVE evidence from the aircraft, and the altitude has to
 * corroborate it: an explicit `is_flying === false` at 40 m does not ground an
 * aircraft either, for the same reason `isAirborne` lets the bar overrule it in
 * that direction.
 *
 * Added for #505 slice 10 (D45), which used `!isAirborne(drone)` for the landing
 * verbs and so removed LAND and CONFIRM LANDING from every MAVLink aircraft
 * below 2 m - including one held at ~1 m by DJI landing protection, which is the
 * exact state those two keys exist to resolve.
 */
export function isPositivelyOnGround(drone: LiveDrone): boolean {
  return drone.is_flying === false && drone.altitude <= AIRBORNE_ABOVE_M;
}

export interface ManualControlAvailability {
  /** False → do not offer the MANUAL key as pressable. */
  available: boolean;
  /** Operator-facing explanation when `available` is false. Never null then. */
  reason: string | null;
}

/**
 * Whether this aircraft can be flown by hand from the COP right now.
 *
 * Ordered most-airframe-y first: there is no point telling an operator to find a
 * tablet for an aircraft that has no manual path at all. Everything after the
 * vendor checks is a transient condition they can act on.
 *
 * The aircraft's `command_secret` IS checked here, via the presence flag the
 * API ships in its place (`commandSecretPresentOf`, #261). The secret itself is
 * still server-side only — the browser never sees it and never could — but its
 * PRESENCE is a verdict the server publishes on every fleet row, in the same
 * class as `commandRoute`. Reading it is not the client re-deriving a vendor
 * rule (the mistake 92c2541 removed); it is the client reading an answer the
 * server already gave. Without a secret the companion app cannot sign, so the
 * backend refuses `control_request` with MANUAL_REFUSAL.noSecret — refusing
 * here just turns that round trip into an immediate answer, which is what every
 * other gate in this ladder does.
 */
export function manualControlAvailability({
  drone,
  manufacturer,
  isCompanion = false,
  registryKnown = true,
  commandSecretPresent = null,
  canOperate,
  companionOnline = null,
  heldBy = null,
  pilotOverride = false,
  surfaceControllable = true,
  surfaceRefusal = null,
  portrait = false,
  pointer = 'desktop',
  deviceRefusal = null,
  now = Date.now(),
}: {
  drone: LiveDrone;
  manufacturer?: string | null;
  /** drones.integration_config.companion — the DJI MSDK path. */
  isCompanion?: boolean;
  /** False while the registry is loading or after it failed. */
  registryKnown?: boolean;
  /**
   * From `commandSecretPresentOf(registry row)` (#261). Null means UNKNOWN and
   * gates nothing; only an explicit `false` refuses, for the same reason it
   * only blocks the flight bar on `false` — telling an operator their aircraft
   * cannot be hand-flown because a fetch has not landed is a false statement
   * about their aircraft.
   */
  commandSecretPresent?: boolean | null;
  /** Role gate — admin or operator. */
  canOperate: boolean;
  /** Companion-app liveness. Null when nothing has reported either way. */
  companionOnline?: boolean | null;
  /** Another operator's session, when one is known. `since` is `HH:mm` UTC. */
  heldBy?: { operator: string; since: string } | null;
  /** The RC reports a human on the sticks. */
  pilotOverride?: boolean;
  /** ringGeometry().controllable for the surface the operator would fly on. */
  surfaceControllable?: boolean;
  /**
   * `ringGeometry().refusal` for that same surface — the MEASUREMENT that
   * failed ("Control surface is 210 px — manual flight needs 240"), which is
   * something the operator can act on by giving the map column its width back.
   * Falls back to the device sentence only when the caller has no measurement
   * to offer.
   */
  surfaceRefusal?: string | null;
  /**
   * The surface is taller than it is wide (`isPortrait`, from `useManualDevice`).
   *
   * Checked HERE and not only on the control surface, which is where it used to
   * live alone: the surface renders a rotate prompt with `interactive=false`, but
   * by the time it does that the COP has already asked for authority and the RC
   * has already locked the person standing next to the aircraft out of the
   * sticks. Taking an aircraft away from somebody and then offering no control is
   * the worst outcome this feature has, so the refusal has to come before the
   * request, not after it.
   */
  portrait?: boolean;
  /** Which pointer is driving — it decides the WORDING of the portrait refusal. */
  pointer?: PointerMode;
  /**
   * Why this DEVICE or BROWSER cannot fly, from `useManualDevice`: a phone, or a
   * browser without pointer events / `touch-action` / `setPointerCapture`. Null
   * when there is nothing wrong with it. Kept as a passed-in string rather than
   * detected here so this module stays pure and testable without a window.
   */
  deviceRefusal?: string | null;
  now?: number;
}): ManualControlAvailability {
  const refuse = (reason: string): ManualControlAvailability => ({ available: false, reason });

  if (!canOperate) return refuse(MANUAL_REFUSAL.role);

  // Same rule as the command bar's manufacturer gating: with no registry row the
  // vendor is unknown, and "DJI companion only" would be a claim about an
  // airframe nobody has read yet. Refuse, but say which question is unanswered.
  if (!registryKnown) {
    return refuse('Aircraft type not loaded yet — manual flight stays refused until the registry answers.');
  }
  if (manufacturer !== 'dji' || !isCompanion) return refuse(MANUAL_REFUSAL.notCompanion);

  // With the airframe checks and not the transient ones below, because a missing
  // signing secret is a provisioning fault rather than something the operator can
  // wait out. Mirrors the flight bar's #261 gate: the companion cannot sign, so
  // MissionController refuses every motor command — and manual flight is nothing
  // BUT a stream of motor commands, which makes this the one gate whose absence
  // would hand somebody an authority lock they can do nothing with.
  if (commandSecretPresent === false) return refuse(MANUAL_REFUSAL.noSecret);

  // Only an explicit offline reading refuses. A drone with no companion record
  // at all is an older app build that has never sent a heartbeat, which is not
  // evidence it is dead — the RC's own grant/deny settles that.
  if (companionOnline === false) return refuse(MANUAL_REFUSAL.companionOffline);

  if (heldBy) return refuse(refusalHeldBy(heldBy.operator, heldBy.since));
  if (pilotOverride) return refuse(MANUAL_REFUSAL.pilotHasSticks);

  const ageMs = now - new Date(drone.last_seen_at).getTime();
  if (!Number.isFinite(ageMs) || ageMs > MANUAL_LIMITS.TELEMETRY_STALE_MS) {
    // There is no position to drag a vector out of. The staleness window here is
    // 3 s, far tighter than linkFreshness's 30 s, because a 3-second-old fix is
    // already 15 m of travel at the envelope's top speed.
    return refuse(refusalStaleTelemetry(Number.isFinite(ageMs) ? ageMs / 1000 : 0));
  }

  // Airborne only, by the aircraft's own answer where it has one. Offering the key does not
  // stream anything: the request still has to pass the server and then the RC's own `!isFlying`
  // grant, so a wrong yes here costs a refusal the operator can read, while a wrong no greys out
  // the control for an aircraft that is genuinely in the air. See `isAirborne`.
  if (!isAirborne(drone)) return refuse(MANUAL_REFUSAL.onGround);

  // Last, because they are the refusals the operator can do least about in the
  // moment, and because there is no point telling somebody to find a tablet for
  // an aircraft that is sitting on the ground. A browser without pointer capture
  // is not degradable — there is no control to degrade TO — so it is refused
  // outright rather than offered in some reduced form.
  if (deviceRefusal) return refuse(deviceRefusal);

  // Before `surfaceControllable`, because a portrait window usually has a map
  // column that passes the width test — it is the SHAPE that has no control
  // layout, and "needs a tablet or larger" would send an operator looking for a
  // bigger screen they already have.
  if (portrait) return refuse(manualPortraitRefusal(pointer));

  // The measurement, not a device verdict: this refusal fires on a 27-inch
  // monitor whose map column was narrowed by the details panel, and "find a
  // tablet" names the wrong thing to change. A phone is refused above, by
  // `deviceRefusal`, which is where the device sentence belongs.
  if (!surfaceControllable) return refuse(surfaceRefusal ?? MANUAL_REFUSAL.viewportTooNarrow);

  return { available: true, reason: null };
}

/**
 * Is manual flight released to operators?
 *
 * The whole subsystem is built and wired - the ladder above, the control
 * surface, the authority hold, the RC's own grant - but it has never been flown
 * against a real aircraft, and an operator who takes authority is locking the
 * person standing next to that aircraft out of the sticks. Until that has been
 * proven in the field, the COP offers the capability without arming it: the
 * MANUAL key stays on the bar, greyed, saying why.
 *
 * Typed `boolean` rather than left as the literal `false` on purpose, so that
 * flipping it is a one-character edit and nothing downstream was ever narrowed
 * into treating the released path as dead code.
 *
 * To release: set this to `true`. `manual-flight-release.test.ts` fails on that
 * change by design - the tripwire is there so the flip is a decision somebody
 * makes, not something a refactor does quietly.
 */
export const MANUAL_FLIGHT_RELEASED: boolean = false;

/**
 * What the COP offers for manual control - the release switch over the ladder.
 *
 * Every surface that can START a manual session reads this rather than
 * `manualControlAvailability` directly, so there is one place the switch has to
 * hold: the MANUAL key on the command bar, the TAKE MANUAL key on the fly-to
 * outcome note, and the `available` the session hook gates `request` on.
 *
 * The switch does NOT simply outrank the ladder. Two things beat it, for two
 * different reasons:
 *
 * 1. `heldBySelf` - a session this console is already holding. A switch that
 *    turns a feature off must not be able to strand somebody inside it. The
 *    dangerous case is not the live session (the hook keeps a held one out of
 *    `UNAVAILABLE`, so the hand-back stays pressable) but a RELOAD whose
 *    re-adoption is refused - a lock with no resumable RC frame, an `override`
 *    or `fault` authority, a drone-detail fetch that failed. The lock is still
 *    ours on the server, and pressing MANUAL to take a clean one is the way
 *    back that `useManualControl` documents. Greying that key would leave a
 *    real aircraft held by a console that can no longer reach it.
 *
 * 2. The refusals that are true whether or not this ever ships - `role` and
 *    `notCompanion`. A viewer will never hand-fly anything, and a MAVLink,
 *    Skydio or dock aircraft has no manual path at all; telling either of them
 *    "not released yet" promises a capability that is not coming. The ladder's
 *    own sentence is the honest one, and it is what they read today.
 *
 * Everything else the ladder would refuse is TRANSIENT - on the ground, stale
 * telemetry, a narrow window, a companion that is offline - and those lose to
 * the switch on purpose: a greyed key saying "use TAKEOFF first" sends somebody
 * to launch an aircraft for a capability that is not coming back either way.
 */
export function manualControlOffer(
  args: Parameters<typeof manualControlAvailability>[0] & {
    /**
     * The server's authority lock on this aircraft names THIS signed-in user.
     * From `manual_control.holder.user_id` on the drone-detail response, which
     * is the id the backend's own lock compares - not the display name, which
     * two accounts can share.
     */
    heldBySelf?: boolean;
  },
): ManualControlAvailability {
  const { heldBySelf = false, ...ladderArgs } = args;
  const ladder = manualControlAvailability(ladderArgs);
  if (MANUAL_FLIGHT_RELEASED) return ladder;
  if (heldBySelf) return ladder;
  if (ladder.reason === MANUAL_REFUSAL.role) return ladder;
  if (ladder.reason === MANUAL_REFUSAL.notCompanion) return ladder;
  return { available: false, reason: MANUAL_REFUSAL.notReleased };
}

/**
 * How long an org-wide manual session stays believable after its last frame.
 *
 * The RC publishes `control_state` at 1 Hz while it holds authority, and its own
 * heartbeat dead-man is 10 s — so a session that has said nothing for longer
 * than the dead-man has already been given back at the aircraft, whether or not
 * the `released` frame reached us. Erring the other way would leave an aircraft
 * marked as somebody else's forever on the strength of one lost frame.
 */
const MANUAL_HOLD_BELIEVABLE_MS = MANUAL_LIMITS.HEARTBEAT_TIMEOUT_MS;

/**
 * What the COP can honestly say about a holder it did not authenticate.
 *
 * The RC's own frame carries a `session_id` and no identity, so the backend adds
 * `operator` from what the API recorded when the session was requested. When it
 * is present the refusal names the account, which is the whole point of the
 * marker: two consoles in one room need to know WHO, not just that somebody.
 *
 * This fallback is for the honest unknown — an API restart mid-session, or a
 * session granted by another instance. It says "another operator" rather than
 * guessing, and it must never collapse into "no hold": an unnamed holder is
 * still a holder, and rendering the aircraft as free is how a second operator
 * takes one that somebody else is flying.
 */
export const MANUAL_HOLD_UNKNOWN_OPERATOR = 'another operator';

/**
 * The `heldBy` for `manualControlAvailability`, derived from the org-wide
 * broadcast — or null when nothing credible holds this aircraft.
 *
 * `ownSessionIds` is every session THIS console has held. Without it a console
 * reads its own frames back and refuses the operator their own aircraft, which
 * is the same dead MANUAL key this exists to remove, only more confusing.
 *
 * `selfOperator` closes the half of that hole a session id cannot: after a
 * RELOAD this console has no memory of the session it is still holding, so the
 * broadcast comes back as a stranger's hold and the refusal names the operator
 * reading it. The account is the tie-breaker there, and matching on it is not a
 * liberty the client is taking — the backend's own lock already treats the same
 * user as self (`acquireAuthority` drops and retakes their own key rather than
 * refusing them), so refusing here would be the COP being stricter than the
 * thing it is reporting on.
 */
export function manualHoldRefusal({
  hold,
  ownSessionIds = null,
  selfOperator = null,
  now = Date.now(),
}: {
  hold: ManualControlHold | null | undefined;
  ownSessionIds?: ReadonlySet<string> | null;
  /** The signed-in account. A hold in this name is this operator's own. */
  selfOperator?: string | null;
  now?: number;
}): { operator: string; since: string } | null {
  if (!hold) return null;
  if (ownSessionIds?.has(hold.state.session_id)) return null;
  if (isSameOperator(hold.state.operator, selfOperator)) return null;
  if (now - hold.lastSeenAtMs > MANUAL_HOLD_BELIEVABLE_MS) return null;
  // Trim, because an empty or whitespace-only name renders a refusal that reads
  // as though nobody holds it — worse than admitting we do not know.
  const named = hold.state.operator?.trim();
  return {
    operator: named && named.length > 0 ? named : MANUAL_HOLD_UNKNOWN_OPERATOR,
    since: hhmmUtc(hold.firstSeenAtMs),
  };
}

/**
 * Two operator strings naming the same account.
 *
 * Case-folded and trimmed because one side is an email the user typed at login
 * and the other is whatever the API recorded when the session started; a hold
 * that is genuinely the operator's own must not read as a stranger's over a
 * capital letter. An empty or absent name on either side is never a match — the
 * unknown holder is still a holder.
 */
function isSameOperator(a: string | null | undefined, b: string | null | undefined): boolean {
  const left = a?.trim().toLowerCase();
  const right = b?.trim().toLowerCase();
  if (!left || !right) return false;
  return left === right;
}

/** `HH:mm` UTC — the half of the contract's refusal string that is a clock. */
function hhmmUtc(epochMs: number): string {
  const at = new Date(epochMs);
  if (Number.isNaN(at.getTime())) return '--:--';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`;
}
// ---------------------------------------------------------------------------
// Camera control — a third gate, on the payload rather than the airframe.
// ---------------------------------------------------------------------------

/** One camera control's verdict. `reason` is printed verbatim on the video
 *  surface (mono, uppercase) when the control is withheld. */
export interface CameraControlAvailability {
  available: boolean;
  reason: string | null;
}

export interface CameraCommandAvailability {
  gimbalPitch: CameraControlAvailability;
  gimbalYaw: CameraControlAvailability;
  zoom: CameraControlAvailability;
  /** Whether the lens keys may be offered. One verdict for all three lenses:
   *  the capability says the payload can be TOLD which lens to use, not which
   *  lenses it has — the airframe answers that by refusing, in its own words. */
  lens: CameraControlAvailability;
  /** True when no camera control at all is usable — the video window then shows
   *  a single explanation line instead of a bar of dead controls. */
  noneAvailable: boolean;
  /** The line to print when `noneAvailable`. Null otherwise. */
  unavailableReason: string | null;
}

const CAMERA_ALL_BLOCKED = (reason: string): CameraCommandAvailability => ({
  gimbalPitch: { available: false, reason },
  gimbalYaw: { available: false, reason },
  zoom: { available: false, reason },
  lens: { available: false, reason },
  noneAvailable: true,
  unavailableReason: reason,
});

/**
 * Which camera controls (gimbal slew, zoom, lens selection) the video window may
 * offer, and why not when it may not (issue #244).
 *
 * Deliberately its own function rather than a widening of
 * [droneCommandAvailability]: the flight bar's questions (link freshness, verb
 * subsets, docks) are not this surface's, and camera control has one extra gate
 * nothing else has — the payload's own reported capabilities.
 *
 * Never render-and-fail: unavailable means the control's handlers are not
 * attached and nothing is ever sent. The gates, in order (liveness gates mirror
 * [droneCommandAvailability]'s order and tone — review finding F1 was exactly
 * this function offering camera controls on a dead link the flight bar had
 * already declared lost):
 *  1. role — viewers observe;
 *  2. RemoteID — a one-way broadcast has no control link, and never will;
 *  3. link freshness — a lost telemetry link means nothing sent would be
 *     acknowledged;
 *  4. route — only the DJI companion executor implements camera verbs today,
 *     and unlike the flight bar an UNKNOWN route also withholds (offering a
 *     control we cannot know will fly violates never-render-and-fail);
 *  5. companion liveness — camera verbs fly THROUGH the companion app, so a
 *     companion the sweep flipped offline (or that reported itself offline)
 *     withholds everything even while aircraft telemetry is still fresh;
 *  6. the specific capability flag, from the companion heartbeat. Capabilities
 *     null / absent ⇒ not reported ⇒ nothing offered.
 */
export function cameraCommandAvailability({
  withheldReason,
  commandRoute,
  capabilities,
  canOperate,
  telemetrySource,
  lastSeenAt,
  companion,
  now = Date.now(),
}: {
  /**
   * A reason the SURFACE has no controls that says nothing about the aircraft.
   *
   * Checked before every gate below, because those all describe the airframe,
   * the link or the operator's role, and answering "why are there no controls
   * here" with one of them when the real answer is "this window is not the
   * aircraft you have selected" states a falsehood about the aircraft. The COP's
   * floating camera windows outlive their selection, so this case exists.
   *
   * Absent for the FEED segment and the manual stage, which only ever draw the
   * selected aircraft and so can never be in it.
   */
  withheldReason?: string | null;
  /** From `commandRouteOf(registry row)`; null = not known (loading/failed). */
  commandRoute: CommandRoute | null;
  /** From the companion heartbeat. Null/undefined = not reported. */
  capabilities: PayloadCapabilities | null | undefined;
  /** Role gate — admin or operator. */
  canOperate: boolean;
  /** `LiveDrone.telemetry_source` — 'remoteid' is observation-only, always. */
  telemetrySource?: LiveDrone['telemetry_source'];
  /** `LiveDrone.last_seen_at`, for the same freshness gate the flight bar runs.
   *  Undefined/null = no live track bound; the freshness gate is skipped rather
   *  than inventing a verdict about a link we are not measuring. */
  lastSeenAt?: string | null;
  /** Companion-app liveness (the CompanionStatus the COP already tracks).
   *  Null/undefined = no companion row — the capability gate below still
   *  withholds, since capabilities only ever arrive on a companion heartbeat. */
  companion?: Pick<CompanionStatus, 'is_online' | 'stale'> | null;
  now?: number;
}): CameraCommandAvailability {
  if (withheldReason) {
    return CAMERA_ALL_BLOCKED(withheldReason);
  }
  if (!canOperate) {
    return CAMERA_ALL_BLOCKED('ROLE CANNOT COMMAND — VIEW ONLY');
  }
  if (telemetrySource === 'remoteid') {
    return CAMERA_ALL_BLOCKED('REMOTEID BROADCAST TRACK — NO CONTROL LINK');
  }
  if (lastSeenAt != null && linkFreshness(lastSeenAt, now) === 'lost') {
    return CAMERA_ALL_BLOCKED('TELEMETRY LOST — COMMANDS WOULD NOT BE ACKNOWLEDGED');
  }
  if (commandRoute === null) {
    return CAMERA_ALL_BLOCKED('COMMAND ROUTE NOT RESOLVED');
  }
  if (commandRoute !== 'dji_companion') {
    return CAMERA_ALL_BLOCKED('CAMERA CONTROL REQUIRES THE DJI COMPANION');
  }
  // `stale` is set when the staleness sweep — not the app — flipped it offline;
  // both flags mean the same thing here: the app that would relay these verbs
  // is not answering.
  if (companion != null && (!companion.is_online || companion.stale === true)) {
    return CAMERA_ALL_BLOCKED('COMPANION LINK LOST — COMMANDS WOULD NOT BE ACKNOWLEDGED');
  }
  if (capabilities === null || capabilities === undefined) {
    return CAMERA_ALL_BLOCKED('PAYLOAD CAPABILITY NOT REPORTED');
  }

  const control = (flag: boolean, name: string): CameraControlAvailability =>
    flag === true
      ? { available: true, reason: null }
      : { available: false, reason: `${name} NOT SUPPORTED BY THIS PAYLOAD` };

  const gimbalPitch = control(capabilities.gimbal_pitch === true, 'GIMBAL PITCH');
  const gimbalYaw = control(capabilities.gimbal_yaw === true, 'GIMBAL YAW');
  const zoom = control(capabilities.zoom === true, 'ZOOM');
  // `=== true` matters more here than for its siblings: `lens` is optional on
  // the wire (every APK now on an RC predates it), so undefined is the common
  // case and must read as NOT SUPPORTED rather than as anything truthy.
  const lens = control(capabilities.lens === true, 'LENS SELECTION');
  const noneAvailable =
    !gimbalPitch.available && !gimbalYaw.available && !zoom.available && !lens.available;

  return {
    gimbalPitch,
    gimbalYaw,
    zoom,
    lens,
    noneAvailable,
    unavailableReason: noneAvailable ? 'PAYLOAD REPORTS NO GIMBAL, ZOOM OR LENS' : null,
  };
}
